/**
 * Firebase Cloud Messaging — wrapper client-side, e o DONO ÚNICO do canal de
 * push do client. Duas implementações atrás da mesma porta:
 *
 *  - **Web/PWA**: `firebase/messaging` + service worker + VAPID (o caminho
 *    provado em produção desde 03/08).
 *  - **App nativo (iOS/Android)**: `@capacitor-firebase/messaging` via a
 *    fachada `nativeMessaging.ts` — APNs/FCM pelo sistema, sem service worker
 *    e sem VAPID. Entrou em 21/08; o token que sai daqui é FCM nas duas
 *    plataformas, então `registerFcmToken`/`sendPushToUser` não mudam.
 *
 * Quem decide a plataforma é ESTE módulo. Consumidor (hook, tela, watcher)
 * nunca pergunta `isNativeApp()` para escolher caminho de push — duas telas
 * escolhendo por conta própria é a doença do BUG-81.
 *
 * Responsabilidades:
 *  - Detectar suporte (browser sem service worker / iOS Safari fora de PWA /
 *    build nativo sem o plugin).
 *  - Ler e solicitar permissão de notificação (`getNotificationPermissionState`
 *    é a leitura certa nos dois mundos — `Notification.permission` NÃO existe
 *    no WKWebView).
 *  - Obter o registration token (web: VAPID key do Console → Cloud Messaging
 *    → Web Push Certificates; nativo: plugin).
 *  - Receber mensagens em foreground e o toque na notificação.
 *
 * Importação dinâmica do `firebase/messaging` para não inflar o bundle
 * principal — só carrega quando o usuário ativa notificações.
 */

import type { Messaging } from "firebase/messaging";
import { firebaseApp } from "./config";
import { isNativeApp } from "../platform/native";
import {
  isNativePushRuntime,
  nativeCheckPermission,
  nativeGetToken,
  nativeOnForegroundMessage,
  nativeOnNotificationTap,
  nativeRequestPermission,
  type NativePushMessage,
} from "./nativeMessaging";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

let cachedMessaging: Messaging | null = null;

/**
 * `true` quando o ambiente comporta FCM Web:
 *  - service worker disponível
 *  - PushManager disponível (Safari < 16.4 fora de PWA falha aqui)
 *  - Notification API disponível
 *
 * NÃO valida permissão concedida — apenas o suporte do runtime.
 */
export async function isFcmSupported(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  // App nativo: FCM **Web** não vale aqui — o `build:mobile` roda com
  // `VITE_ENABLE_PWA=false`, não executa o `build-fcm-sw` e o
  // `/firebase-messaging-sw.js` nem existe no bundle empacotado. Quem responde
  // é o plugin (`@capacitor-firebase/messaging`, 21/08): presente na bridge,
  // o push nativo é o caminho; ausente (build anterior à frente), a resposta
  // segue "não suportado" e a tela de Ajustes mantém a frase honesta do
  // BUG-133 em vez de degradar sujo registrando um SW inexistente.
  if (isNativeApp()) return isNativePushRuntime();
  if (!("serviceWorker" in navigator)) return false;
  if (!("PushManager" in window)) return false;
  if (typeof Notification === "undefined") return false;
  try {
    const { isSupported } = await import("firebase/messaging");
    return await isSupported();
  } catch {
    return false;
  }
}

/** Instância do FCM **Web** — nunca no app nativo (lá o canal é o plugin). */
async function getMessagingInstance(): Promise<Messaging | null> {
  if (isNativeApp()) return null;
  if (cachedMessaging) return cachedMessaging;
  const supported = await isFcmSupported();
  if (!supported) return null;
  const { getMessaging } = await import("firebase/messaging");
  cachedMessaging = getMessaging(firebaseApp);
  return cachedMessaging;
}

/**
 * Estado ATUAL da permissão, sem pedir nada — a leitura que vale nos dois
 * mundos. `Notification.permission` não existe no WKWebView (e o valor de lá
 * seria o da web, não o do sistema); no nativo quem sabe é o plugin.
 *
 * Web sem a API devolve `default`, não `denied`: afirmar que a pessoa negou
 * algo que o runtime nem oferece mentiria na tela de Ajustes (o ramo
 * `unsupported` é quem cobre esse caso).
 */
export async function getNotificationPermissionState(): Promise<NotificationPermission> {
  if (isNativeApp()) return nativeCheckPermission();
  if (typeof Notification === "undefined") return "default";
  return Notification.permission;
}

/**
 * Solicita permissão (se ainda não foi tomada). Retorna o estado final.
 *
 * Atalho prudente: se já foi `denied`, NÃO repede — browsers bloqueiam
 * reprompt e iOS persiste a decisão. Cliente que quiser educar o usuário
 * deve mostrar tutorial de como reativar nas settings do browser/sistema.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (isNativeApp()) {
    const current = await nativeCheckPermission();
    if (current !== "default") return current;
    return nativeRequestPermission();
  }
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  return await Notification.requestPermission();
}

/**
 * Registration token do FCM como TRANSPORTE — sem pedir permissão de alertas.
 *
 * Existe para o push de conclusão da Live Activity (blueprint
 * `focus-live-activity-push.md` §3.2): o FCM exige o token do aparelho como alvo
 * da mensagem, mas o que chega é um update de Live Activity, não uma
 * notificação — e pedir permissão de alertas aqui seria o convite genérico que
 * o produto já aprendeu a não fazer (BUG-133). O APNs registra o device token
 * sem permissão; se o plugin devolver `null` sem ela, é a premissa do §8.1 do
 * blueprint caindo, e o servidor registra `no_fcm_token`.
 *
 * Só nativo: na web não há Live Activity, e o token web exige VAPID + service
 * worker (`fetchFcmToken`).
 */
export async function fetchFcmTransportToken(): Promise<string | null> {
  if (!isNativeApp() || !isNativePushRuntime()) return null;
  return nativeGetToken();
}

/**
 * Tenta obter o registration token deste device. Retorna `null` quando:
 *  - FCM não é suportado neste runtime.
 *  - VAPID key não está configurado (env ausente).
 *  - Permissão foi negada.
 *  - SW `/firebase-messaging-sw.js` não está disponível (404).
 */
export async function fetchFcmToken(): Promise<string | null> {
  // Nativo: permissão pelo sistema, token pelo plugin. VAPID e service worker
  // são peças do caminho web e não entram aqui.
  if (isNativeApp()) {
    if (!isNativePushRuntime()) return null;
    const permission = await requestNotificationPermission();
    if (permission !== "granted") return null;
    return nativeGetToken();
  }
  if (!VAPID_KEY) {
    console.warn("[Lura] VITE_FIREBASE_VAPID_KEY ausente — FCM não vai funcionar.");
    return null;
  }
  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const permission = await requestNotificationPermission();
  if (permission !== "granted") return null;

  // SW registration explícito (evita race com VitePWA que registra `sw.js`).
  let registration: ServiceWorkerRegistration | undefined;
  try {
    registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    await navigator.serviceWorker.ready;
  } catch (err) {
    console.warn("[Lura] firebase-messaging-sw.js não pôde ser registrado.", err);
    return null;
  }

  try {
    const { getToken } = await import("firebase/messaging");
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (err) {
    console.warn("[Lura] getToken falhou:", err);
    return null;
  }
}

/**
 * Assina mensagens em foreground (aba aberta). O service worker NÃO
 * dispara em foreground — então a UI mostra um toast.
 *
 * Retorna função de unsubscribe.
 */
export async function onForegroundMessage(
  cb: (payload: { title?: string; body?: string; type?: string; url?: string }) => void,
): Promise<() => void> {
  if (isNativeApp()) return nativeOnForegroundMessage(cb);
  const messaging = await getMessagingInstance();
  if (!messaging) return () => undefined;
  const { onMessage } = await import("firebase/messaging");
  return onMessage(messaging, (payload) => {
    cb({
      title: payload.notification?.title,
      body: payload.notification?.body,
      type: payload.data?.type,
      url: payload.data?.url,
    });
  });
}

/**
 * TOQUE na notificação — só existe no app nativo. Na web quem roteia o toque
 * é o service worker (`firebase-messaging-sw.js` lê `data.url`); aqui o
 * equivalente é o listener do plugin, e o consumidor único é o
 * `NativePushTapNavigator` montado no App.
 *
 * Retorna função de unsubscribe; na web é no-op.
 */
export async function onNotificationTap(
  cb: (payload: NativePushMessage) => void,
): Promise<() => void> {
  if (!isNativeApp()) return () => undefined;
  return nativeOnNotificationTap(cb);
}
